import type { ReactNode, JSX } from 'react';
import { Root as CollapsibleRoot } from '@radix-ui/react-collapsible';
import { isNotNil } from '@wistia/type-guards';
import { styled } from 'styled-components';

const StyledRoot = styled(CollapsibleRoot)`
  &[data-state='closed'] [data-wui-collapsible-content] {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: var(--wui-collapsible-content-clamp-lines);
    overflow: hidden;
  }
`;

export type CollapsibleProps = {
  /**
   * `CollapsibleTrigger` and `CollapsibleContent` must be included somewhere inside these children.
   */
  children: ReactNode;
  /**
   * Whether the collapsible content is open or closed. If not provided, the collapsible will be uncontrolled.
   */
  isOpen?: boolean;
  /**
   * Callback for when the trigger is toggled. If not provided, the collapsible will be uncontrolled.
   */
  onOpenChange?: (isOpen: boolean) => void;
};

/**
 * Toggle the visibility of content with a trigger.
 */
export const Collapsible = ({
  children,
  isOpen = false,
  onOpenChange,
}: CollapsibleProps): JSX.Element => {
  const controlProps = {
    ...(isNotNil(onOpenChange) && isNotNil(isOpen) ? { open: isOpen, onOpenChange } : {}),
  };

  return <StyledRoot {...controlProps}>{children}</StyledRoot>;
};

Collapsible.displayName = 'Collapsible';
